/**
 * Enhanced Insights Service
 * Analyzes user's meal data to generate personalized glycemic insights
 * Uses hybrid approach: rule-based analysis + AI text generation
 */

import AsyncStorage from '@/lib/asyncStorage';
import { Firebase } from './firebase/init';
import type { Meal } from '@/types';

// Cloud function for AI insights
const generateInsightsFn = Firebase.functions.httpsCallable('generateInsights');

const INSIGHTS_CACHE_KEY = 'glycemic_insights_cache';
const INSIGHTS_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/** Per-user cache key so one account's insights never leak to another */
function getInsightsCacheKey(uid: string): string {
  return `${INSIGHTS_CACHE_KEY}_${uid}`;
}

// ============================================
// Public Types
// ============================================

export interface MealInsight {
  type: 'trigger' | 'safe' | 'pattern' | 'emerging' | 'tip' | 'stacking' | 'response_pattern' | 'timing';
  emoji: string;
  title: string;
  description: string;
}

export interface InsightsData {
  insights: MealInsight[];
  lastUpdated: Date;
  mealsAnalyzed: number;
  responsesTracked: number;
}

// ============================================
// Internal Analysis Types
// ============================================

interface TriggerAnalysis {
  food: string;
  giCategories: string[];
  badMeals: number;
  goodMeals: number;
  totalMeals: number;
  badRate: number;
  differentialScore: number;
  responses: { [response: string]: number };
  confidence: 'high' | 'medium' | 'low';
}

interface SafeFoodAnalysis {
  food: string;
  goodMeals: number;
  badMeals: number;
  totalMeals: number;
  goodRate: number;
  differentialScore: number;
  confidence: 'high' | 'medium' | 'low';
}

interface StackingAnalysis {
  categories: string[];
  aloneGoodRate: number;
  combinedBadRate: number;
  mealsAnalyzed: number;
  exampleFoods: string[];
}

interface ResponsePatternAnalysis {
  response: string;
  topTriggers: { food: string; rate: number; count: number }[];
  giCorrelation: { category: string; rate: number }[];
}

interface TimingAnalysis {
  avgOnsetMinutes: number;
  onsetDistribution: { within_hour: number; '2-4_hours': number; '5+_hours': number };
  morningIssues: number;
  eveningIssues: number;
  totalBadMeals: number;
}

export interface AnalysisResult {
  triggers: TriggerAnalysis[];
  safeFoods: SafeFoodAnalysis[];
  stackingPatterns: StackingAnalysis[];
  responsePatterns: ResponsePatternAnalysis[];
  timingPatterns: TimingAnalysis;
  mealsAnalyzed: number;
  responsesTracked: number;
  confidenceLevel: 'low' | 'medium' | 'high';
}

interface CachedInsights {
  data: InsightsData;
  cachedAt: number;
  mealsHash: string;
}

// ============================================
// Food Tracking Helpers
// ============================================

interface FoodStats {
  total: number;
  good: number; // stable or mild_rise
  bad: number;  // moderate_rise or spike
  goodRate: number;           // good / total
  badRate: number;            // bad / total
  differentialScore: number;  // badRate - goodRate (range: -1 to +1)
  responses: { [response: string]: number };
  giCategories: Set<string>;
  giLevel: 'low' | 'medium' | 'high' | null;
  hasHighGI: boolean; // True if food is medium or high GI
}

// ============================================
// Low-GI Safelist
// ============================================

/**
 * Foods that are inherently low-GI and should rarely be flagged as triggers
 * Even if they appear in bad meals, they're likely coincidental
 */
const LOW_GI_SAFELIST = [
  'chicken', 'beef', 'pork', 'lamb', 'fish', 'salmon', 'tuna', 'shrimp', 'prawns',
  'eggs', 'egg', 'broccoli', 'cauliflower', 'spinach', 'kale', 'lettuce',
  'cucumber', 'zucchini', 'courgette', 'asparagus', 'green beans',
  'bell pepper', 'capsicum', 'olive oil', 'butter', 'bacon', 'turkey', 'duck',
  'tofu', 'tempeh', 'eggplant', 'aubergine', 'mushroom', 'onion', 'garlic',
  'avocado', 'nuts', 'almonds', 'walnuts', 'macadamia', 'pecans', 'pine nuts',
  'cheese', 'cream', 'sour cream', 'heavy cream',
  'strawberry', 'strawberries', 'blueberry', 'blueberries', 'raspberry', 'raspberries',
  'lemon', 'lime', 'grapefruit', 'cherries', 'apple', 'pear', 'peach', 'plum'
];

/**
 * Check if a food name matches the low-GI safelist
 */
function isLikelySafeFood(name: string): boolean {
  const normalized = name.toLowerCase();
  return LOW_GI_SAFELIST.some(safe => normalized.includes(safe));
}

function getFoodStats(meals: Meal[]): Map<string, FoodStats> {
  const stats = new Map<string, FoodStats>();

  meals.forEach((meal) => {
    if (!meal.glycemicAnalysis?.identifiedFoods || !meal.responseResult) return;

    const isGood = meal.responseResult === 'stable' || meal.responseResult === 'mild_rise';
    const isBad = meal.responseResult === 'spike' || meal.responseResult === 'moderate_rise';
    const energyLevel = meal.response?.energyLevel;

    meal.glycemicAnalysis.identifiedFoods.forEach((food) => {
      const name = food.name.toLowerCase().trim();
      const existing = stats.get(name) || {
        total: 0,
        good: 0,
        bad: 0,
        goodRate: 0,
        badRate: 0,
        differentialScore: 0,
        responses: {},
        giCategories: new Set<string>(),
        giLevel: null,
        hasHighGI: false,
      };

      existing.total++;
      if (isGood) existing.good++;
      if (isBad) existing.bad++;

      // Track which energy responses occurred with this food
      if (isBad && energyLevel) {
        existing.responses[energyLevel] = (existing.responses[energyLevel] || 0) + 1;
      }

      // Track GI level from food
      if (food.giLevel) {
        existing.giLevel = food.giLevel;
        if (food.giLevel === 'medium' || food.giLevel === 'high') {
          existing.hasHighGI = true;
        }
        // Map GI level to categories
        if (food.giLevel === 'high') {
          existing.giCategories.add('highGI');
        } else if (food.giLevel === 'medium') {
          existing.giCategories.add('mediumGI');
        } else {
          existing.giCategories.add('lowGI');
        }
      }

      stats.set(name, existing);
    });
  });

  // Calculate rates and differential scores after all data is collected
  stats.forEach((stat) => {
    if (stat.total > 0) {
      stat.goodRate = stat.good / stat.total;
      stat.badRate = stat.bad / stat.total;
      stat.differentialScore = stat.badRate - stat.goodRate;
    }
  });

  return stats;
}

/**
 * Calculate confidence level based on differential scoring
 * Uses differential score instead of simple bad rate for more accurate assessment
 */
function getConfidence(stat: FoodStats): 'high' | 'medium' | 'low' {
  const { total, differentialScore, bad } = stat;

  // High confidence: 5+ meals, strong differential (0.6+), 3+ bad occurrences
  if (total >= 5 && differentialScore >= 0.6 && bad >= 3) {
    return 'high';
  }

  // Medium confidence: 3+ meals, moderate differential (0.4+), 2+ bad occurrences
  if (total >= 3 && differentialScore >= 0.4 && bad >= 2) {
    return 'medium';
  }

  // Low confidence: emerging pattern, needs more data
  return 'low';
}

/**
 * Determine if a food should be flagged as a potential trigger
 * Uses differential scoring to avoid false positives from foods that appear
 * equally in good and bad meals (like grilled chicken)
 */
function isTriggerCandidate(stat: FoodStats): boolean {
  // Must have differential score > 0.3 (more bad than good)
  // This filters out foods like chicken that appear equally in good/bad meals
  if (stat.differentialScore <= 0.3) {
    return false;
  }

  // Must have at least 1 bad occurrence
  if (stat.bad < 1) {
    return false;
  }

  // Must have high or medium GI (skip low-GI foods)
  if (!stat.hasHighGI) {
    return false;
  }

  return true;
}

/**
 * Calculate a relevance score for a food based on GI level and user sensitivities
 * Higher score = more likely to be a real trigger
 */
function getGIRelevanceScore(foodStats: FoodStats): number {
  // Foods with low GI should not be blamed for blood sugar issues
  if (!foodStats.hasHighGI) {
    return 0;
  }

  // Base score for GI level
  let score = 0;
  if (foodStats.giLevel === 'high') {
    score = 3;
  } else if (foodStats.giLevel === 'medium') {
    score = 2;
  }

  return score;
}

// ============================================
// Analyzers
// ============================================

function analyzeTriggers(meals: Meal[]): TriggerAnalysis[] {
  const stats = getFoodStats(meals);
  const triggers: TriggerAnalysis[] = [];

  stats.forEach((stat, food) => {
    // Use the isTriggerCandidate function which checks:
    // 1. Differential score > 0.3 (more bad than good meals)
    // 2. At least 1 bad occurrence
    // 3. Has medium or high GI
    if (!isTriggerCandidate(stat)) {
      return;
    }

    // Additional check: skip foods on the safelist that somehow got flagged
    if (isLikelySafeFood(food) && stat.differentialScore < 0.5) {
      return;
    }

    triggers.push({
      food,
      giCategories: Array.from(stat.giCategories),
      badMeals: stat.bad,
      goodMeals: stat.good,
      totalMeals: stat.total,
      badRate: stat.badRate,
      differentialScore: stat.differentialScore,
      responses: stat.responses,
      confidence: getConfidence(stat),
    });
  });

  // Sort by: differential score (desc) > GI relevance > confidence
  return triggers
    .sort((a, b) => {
      // Sort by differential score first (higher = more likely trigger)
      if (b.differentialScore !== a.differentialScore) {
        return b.differentialScore - a.differentialScore;
      }

      // Then by GI relevance
      const aStats = stats.get(a.food);
      const bStats = stats.get(b.food);
      const aRelevance = aStats ? getGIRelevanceScore(aStats) : 0;
      const bRelevance = bStats ? getGIRelevanceScore(bStats) : 0;
      if (bRelevance !== aRelevance) {
        return bRelevance - aRelevance;
      }

      // Then by confidence
      const confOrder = { high: 0, medium: 1, low: 2 };
      return confOrder[a.confidence] - confOrder[b.confidence];
    })
    .slice(0, 5); // Top 5 triggers
}

function analyzeSafeFoods(meals: Meal[]): SafeFoodAnalysis[] {
  const stats = getFoodStats(meals);
  const safeFoods: SafeFoodAnalysis[] = [];

  stats.forEach((stat, food) => {
    // Use differential score: negative means more good than bad
    // A food is "safe" if differential score is <= -0.3 (more good than bad)
    // AND has been eaten at least 2 times
    // AND has at least 1 good outcome
    if (stat.differentialScore <= -0.3 && stat.total >= 2 && stat.good >= 1) {
      // Calculate confidence for safe foods
      // High: very negative differential, many meals
      // Medium: moderately negative, some meals
      // Low: slight negative, few meals
      let confidence: 'high' | 'medium' | 'low' = 'low';
      if (stat.total >= 5 && stat.differentialScore <= -0.6 && stat.good >= 4) {
        confidence = 'high';
      } else if (stat.total >= 3 && stat.differentialScore <= -0.4 && stat.good >= 2) {
        confidence = 'medium';
      }

      safeFoods.push({
        food,
        goodMeals: stat.good,
        badMeals: stat.bad,
        totalMeals: stat.total,
        goodRate: stat.goodRate,
        differentialScore: stat.differentialScore,
        confidence,
      });
    }
  });

  // Sort by differential score (most negative = most safe) then confidence
  return safeFoods
    .sort((a, b) => {
      // Most negative differential first (safest foods)
      if (a.differentialScore !== b.differentialScore) {
        return a.differentialScore - b.differentialScore;
      }
      // Then by confidence
      const confOrder = { high: 0, medium: 1, low: 2 };
      return confOrder[a.confidence] - confOrder[b.confidence];
    })
    .slice(0, 5); // Top 5 safe foods
}

function analyzeStacking(meals: Meal[]): StackingAnalysis[] {
  // Track meals by GI level combinations
  const giCombinations = new Map<string, { good: number; bad: number; foods: Set<string> }>();
  const singleGI = new Map<string, { good: number; bad: number }>();

  meals.forEach((meal) => {
    if (!meal.glycemicAnalysis?.identifiedFoods || !meal.responseResult) return;

    const isGood = meal.responseResult === 'stable' || meal.responseResult === 'mild_rise';
    const isBad = meal.responseResult === 'spike' || meal.responseResult === 'moderate_rise';

    // Collect all GI levels in this meal
    const mealGILevels = new Set<string>();
    const mealFoods = new Set<string>();

    meal.glycemicAnalysis.identifiedFoods.forEach((food) => {
      if (food.giLevel) {
        mealGILevels.add(food.giLevel);
        mealFoods.add(food.name);
      }
    });

    const giArray = Array.from(mealGILevels).sort();

    if (giArray.length === 1) {
      // Single GI level meal
      const gi = giArray[0];
      const existing = singleGI.get(gi) || { good: 0, bad: 0 };
      if (isGood) existing.good++;
      if (isBad) existing.bad++;
      singleGI.set(gi, existing);
    } else if (giArray.length >= 2) {
      // Multiple GI levels - check pairwise combinations
      for (let i = 0; i < giArray.length; i++) {
        for (let j = i + 1; j < giArray.length; j++) {
          const key = `${giArray[i]}+${giArray[j]}`;
          const existing = giCombinations.get(key) || { good: 0, bad: 0, foods: new Set() };
          if (isGood) existing.good++;
          if (isBad) existing.bad++;
          mealFoods.forEach(f => existing.foods.add(f));
          giCombinations.set(key, existing);
        }
      }
    }
  });

  // Find combinations that are worse than individual GI levels
  const stackingPatterns: StackingAnalysis[] = [];

  giCombinations.forEach((combo, key) => {
    const [gi1, gi2] = key.split('+');
    const single1 = singleGI.get(gi1);
    const single2 = singleGI.get(gi2);

    if (!single1 || !single2) return;

    const comboTotal = combo.good + combo.bad;
    if (comboTotal < 2) return;

    const comboBadRate = combo.bad / comboTotal;
    const single1BadRate = single1.bad / (single1.good + single1.bad) || 0;
    const single2BadRate = single2.bad / (single2.good + single2.bad) || 0;
    const avgSingleBadRate = (single1BadRate + single2BadRate) / 2;

    // Stacking if combination is significantly worse than singles
    if (comboBadRate >= 0.6 && comboBadRate > avgSingleBadRate + 0.2) {
      stackingPatterns.push({
        categories: [gi1, gi2],
        aloneGoodRate: 1 - avgSingleBadRate,
        combinedBadRate: comboBadRate,
        mealsAnalyzed: comboTotal,
        exampleFoods: Array.from(combo.foods).slice(0, 4),
      });
    }
  });

  return stackingPatterns.sort((a, b) => b.combinedBadRate - a.combinedBadRate).slice(0, 3);
}

function analyzeResponsePatterns(meals: Meal[]): ResponsePatternAnalysis[] {
  // Get food stats first to access GI content and differential scores
  const stats = getFoodStats(meals);

  // Track which foods appear with which responses
  const responseFoods = new Map<string, Map<string, { count: number; total: number }>>();

  meals.forEach((meal) => {
    if (!meal.glycemicAnalysis?.identifiedFoods || !meal.response?.energyLevel) return;
    if (meal.responseResult !== 'spike' && meal.responseResult !== 'moderate_rise') return;

    const energyLevel = meal.response.energyLevel;

    if (!responseFoods.has(energyLevel)) {
      responseFoods.set(energyLevel, new Map());
    }
    const foodMap = responseFoods.get(energyLevel)!;

    meal.glycemicAnalysis!.identifiedFoods.forEach((food) => {
      const name = food.name.toLowerCase().trim();

      // Skip foods with low GI
      const foodStat = stats.get(name);
      if (!foodStat?.hasHighGI) {
        return; // Don't track low-GI foods as response triggers
      }

      // Also skip foods on the safelist
      if (isLikelySafeFood(name)) {
        return;
      }

      // Skip foods with low differential score (appear equally in good/bad meals)
      if (foodStat && foodStat.differentialScore <= 0.3) {
        return;
      }

      const existing = foodMap.get(name) || { count: 0, total: 0 };
      existing.count++;
      foodMap.set(name, existing);
    });
  });

  // Update totals (how many times each food appeared in any meal)
  responseFoods.forEach((foodMap) => {
    foodMap.forEach((data, food) => {
      data.total = stats.get(food)?.total || data.count;
    });
  });

  const patterns: ResponsePatternAnalysis[] = [];

  responseFoods.forEach((foodMap, response) => {
    const triggers: { food: string; rate: number; count: number }[] = [];

    foodMap.forEach((data, food) => {
      // Additional GI check when building final triggers
      const foodStat = stats.get(food);
      if (!foodStat?.hasHighGI) return;

      if (data.count >= 2 && data.total >= 2) {
        const rate = data.count / data.total;
        if (rate >= 0.5) {
          triggers.push({ food, rate, count: data.count });
        }
      }
    });

    if (triggers.length > 0) {
      patterns.push({
        response,
        topTriggers: triggers.sort((a, b) => b.rate - a.rate).slice(0, 3),
        giCorrelation: [], // Could add GI category correlation here
      });
    }
  });

  // Sort by number of triggers found
  return patterns.sort((a, b) => b.topTriggers.length - a.topTriggers.length).slice(0, 3);
}

function analyzeTimingPatterns(meals: Meal[]): TimingAnalysis {
  const onsetDistribution = { within_hour: 0, '2-4_hours': 0, '5+_hours': 0 };
  let morningIssues = 0;
  let eveningIssues = 0;
  let totalBadMeals = 0;
  const onsetMinutes: number[] = [];

  meals.forEach((meal) => {
    if (meal.responseResult !== 'spike' && meal.responseResult !== 'moderate_rise') return;
    totalBadMeals++;

    // Track onset time distribution
    if (meal.response?.onsetTime) {
      const onset = meal.response.onsetTime;
      if (onset === 'within_hour') onsetDistribution.within_hour++;
      else if (onset === '2-4_hours') onsetDistribution['2-4_hours']++;
      else if (onset === '5+_hours') onsetDistribution['5+_hours']++;
    }

    // Track time of day (fallback to analyzedAt if eatenAt missing)
    const mealTime = meal.eatenAt || meal.analyzedAt;
    if (mealTime) {
      const hour = new Date(mealTime).getHours();
      if (hour < 12) morningIssues++;
      else eveningIssues++;
    }

    // Track actual minutes (from timeAfterMeal)
    if (meal.response?.timeAfterMeal) {
      onsetMinutes.push(meal.response.timeAfterMeal);
    }
  });

  const avgOnsetMinutes = onsetMinutes.length > 0
    ? onsetMinutes.reduce((a, b) => a + b, 0) / onsetMinutes.length
    : 0;

  return {
    avgOnsetMinutes,
    onsetDistribution,
    morningIssues,
    eveningIssues,
    totalBadMeals,
  };
}

// ============================================
// Main Analysis Function
// ============================================

export function analyzePatterns(meals: Meal[]): AnalysisResult {
  const mealsWithResponses = meals.filter((m) => m.responseResult);

  return {
    triggers: analyzeTriggers(meals),
    safeFoods: analyzeSafeFoods(meals),
    stackingPatterns: analyzeStacking(meals),
    responsePatterns: analyzeResponsePatterns(meals),
    timingPatterns: analyzeTimingPatterns(meals),
    mealsAnalyzed: meals.length,
    responsesTracked: mealsWithResponses.length,
    confidenceLevel: mealsWithResponses.length >= 10 ? 'high' : mealsWithResponses.length >= 5 ? 'medium' : 'low',
  };
}

// ============================================
// AI Text Generation
// ============================================

async function generateAIInsights(analysis: AnalysisResult): Promise<MealInsight[]> {
  try {
    // Skip AI for very limited data - local insights are better for small datasets
    if (analysis.responsesTracked < 5) {
      return generateLocalInsights(analysis);
    }

    // Clean timing data to prevent AI confusion (omit avgOnsetMinutes)
    const cleanedAnalysis = {
      ...analysis,
      timingPatterns: {
        onsetDistribution: analysis.timingPatterns.onsetDistribution,
        // Omit avgOnsetMinutes, morningIssues, eveningIssues to prevent AI confusion
      },
    };
    const result = await generateInsightsFn({ analysis: cleanedAnalysis });
    const data = result.data as {
      success: boolean;
      insights?: MealInsight[];
      useLocal?: boolean;
      error?: string;
    };

    // If server says to use local (AI disabled, no key, or error), fall back
    if (data.useLocal || !data.insights?.length) {
      return generateLocalInsights(analysis);
    }

    return data.insights.slice(0, 5);
  } catch (error) {
    console.error('Cloud insights generation failed, using local:', error);
    return generateLocalInsights(analysis);
  }
}

// ============================================
// Local Fallback
// ============================================

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function generateLocalInsights(analysis: AnalysisResult): MealInsight[] {
  const insights: MealInsight[] = [];

  // Add trigger insights - show high/medium confidence as "trigger", low as "emerging"
  // Use differential scoring context: show "X bad, Y OK" to give full picture
  analysis.triggers.slice(0, 3).forEach((trigger) => {
    const topResponse = Object.entries(trigger.responses)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    const isLowConfidence = trigger.confidence === 'low';
    const { badMeals, goodMeals, totalMeals } = trigger;

    // Build description based on confidence level
    let description: string;
    if (isLowConfidence) {
      // Early signal with context about good meals
      if (goodMeals === 0) {
        description = `Early signal: ${trigger.food} caused spikes ${badMeals}/${badMeals} times. Track more to confirm.`;
      } else {
        description = `Early signal: ${trigger.food} caused spikes ${badMeals}/${totalMeals} times (${goodMeals} ${goodMeals === 1 ? 'time' : 'times'} OK). Track more to confirm.`;
      }
    } else if (trigger.confidence === 'medium') {
      // Medium confidence - likely trigger with some good context
      description = `${capitalize(trigger.food)} is a likely trigger — spikes in ${badMeals}/${totalMeals} meals${goodMeals > 0 ? `, only OK in ${goodMeals}` : ''}.`;
    } else {
      // High confidence - confirmed trigger
      description = topResponse === 'crash'
        ? `${capitalize(trigger.food)} is a confirmed trigger — caused energy crashes ${badMeals}/${totalMeals} times.`
        : `${capitalize(trigger.food)} is a confirmed trigger — caused blood sugar spikes ${badMeals}/${totalMeals} times.`;
    }

    insights.push({
      type: isLowConfidence ? 'emerging' : 'trigger',
      emoji: isLowConfidence ? '🔍' : '📈',
      title: isLowConfidence
        ? `Watching: ${capitalize(trigger.food)}`
        : `${capitalize(trigger.food)} Spikes Blood Sugar`,
      description,
    });
  });

  // Add safe food insights - include low confidence with softer wording
  // Show good/bad meal counts for context
  if (analysis.safeFoods.length > 0) {
    const highConfSafe = analysis.safeFoods.filter((s) => s.confidence !== 'low');
    const lowConfSafe = analysis.safeFoods.filter((s) => s.confidence === 'low');

    // Show high/medium confidence safe foods
    if (highConfSafe.length > 0) {
      if (highConfSafe.length === 1) {
        const safe = highConfSafe[0];
        // Show full context: good vs bad
        const badContext = safe.badMeals > 0 ? ` (only ${safe.badMeals} ${safe.badMeals === 1 ? 'spike' : 'spikes'})` : '';
        insights.push({
          type: 'safe',
          emoji: '✅',
          title: `${capitalize(safe.food)} is Safe`,
          description: `You've eaten ${safe.food} ${safe.totalMeals} times — stable blood sugar ${safe.goodMeals} times${badContext}. A reliable choice!`,
        });
      } else {
        const foodList = highConfSafe.slice(0, 3).map((s) => capitalize(s.food)).join(', ');
        const topSafe = highConfSafe[0];
        insights.push({
          type: 'safe',
          emoji: '✅',
          title: 'Your Safe Foods',
          description: `${foodList} have been consistently blood-sugar friendly (${topSafe.goodMeals}/${topSafe.totalMeals} stable with ${topSafe.food}).`,
        });
      }
    }

    // Show low confidence safe foods as "looking good"
    if (lowConfSafe.length > 0 && insights.length < 4) {
      const topLowSafe = lowConfSafe[0];
      const safeList = lowConfSafe.slice(0, 3).map((s) => capitalize(s.food)).join(', ');
      insights.push({
        type: 'emerging',
        emoji: '👍',
        title: 'Looking Good So Far',
        description: `${safeList} ${lowConfSafe.length === 1 ? 'has' : 'have'} been stable (${topLowSafe.goodMeals}/${topLowSafe.totalMeals} good). Keep tracking to confirm.`,
      });
    }
  }

  // Add stacking pattern
  if (analysis.stackingPatterns.length > 0) {
    const stack = analysis.stackingPatterns[0];
    insights.push({
      type: 'stacking',
      emoji: '📊',
      title: 'Glycemic Stacking Pattern',
      description: `Combining ${capitalize(stack.categories[0])} + ${stack.categories[1]} GI foods caused spikes in ${Math.round(stack.combinedBadRate * 100)}% of meals. Try having them separately.`,
    });
  }

  // Add response pattern
  if (analysis.responsePatterns.length > 0) {
    const pattern = analysis.responsePatterns[0];
    if (pattern.topTriggers.length > 0) {
      const responseName = pattern.response === 'crash' ? 'Energy crash' : 'Blood sugar spike';
      const triggerName = capitalize(pattern.topTriggers[0].food);
      insights.push({
        type: 'response_pattern',
        emoji: getResponseEmoji(pattern.response),
        title: `${responseName} Pattern`,
        description: `Your ${responseName.toLowerCase()}s are often linked to ${triggerName} (${pattern.topTriggers[0].count} times).`,
      });
    }
  }

  // Add timing pattern if meaningful
  const timing = analysis.timingPatterns;
  if (timing.totalBadMeals >= 3) {
    // Check for response onset time pattern (user-reported, reliable data)
    const maxOnset = Math.max(
      timing.onsetDistribution.within_hour,
      timing.onsetDistribution['2-4_hours'],
      timing.onsetDistribution['5+_hours']
    );
    if (maxOnset >= 3) {
      if (timing.onsetDistribution['2-4_hours'] === maxOnset) {
        insights.push({
          type: 'timing',
          emoji: '⏰',
          title: 'Response Timing',
          description: 'Your energy dips usually occur 2-4 hours after eating. This is typical for reactive hypoglycemia after high-GI meals.',
        });
      } else if (timing.onsetDistribution['5+_hours'] === maxOnset) {
        insights.push({
          type: 'timing',
          emoji: '⏰',
          title: 'Delayed Response',
          description: 'Your energy dips often occur 5+ hours after eating. Consider meal spacing and snack timing.',
        });
      } else if (timing.onsetDistribution.within_hour === maxOnset) {
        insights.push({
          type: 'timing',
          emoji: '⏰',
          title: 'Rapid Response',
          description: 'You experience energy changes within an hour of eating. This suggests rapid glucose absorption—focus on low-GI swaps.',
        });
      }
    }
  }

  // Add a tip only if we have zero actual insights
  if (insights.length === 0) {
    if (analysis.responsesTracked < 5) {
      insights.push({
        type: 'tip',
        emoji: '💡',
        title: 'Keep Tracking',
        description: `You're at ${analysis.responsesTracked} tracked meals. ${5 - analysis.responsesTracked} more will unlock better insights!`,
      });
    } else {
      insights.push({
        type: 'tip',
        emoji: '💡',
        title: 'Variety Needed',
        description: 'Try tracking meals with foods you eat repeatedly to identify your personal triggers and safe foods.',
      });
    }
  }

  return insights.slice(0, 5);
}

function getResponseEmoji(response: string): string {
  const emojiMap: { [key: string]: string } = {
    crash: '⚡',
    low: '📉',
    normal: '➡️',
    high: '📈',
  };
  return emojiMap[response] || '📊';
}

// ============================================
// Cache Helpers
// ============================================

function generateCacheKey(meals: Meal[]): string {
  const trackedCount = meals.filter((m) => m.responseResult).length;
  const lastMealId = meals[0]?.id || '';
  return `${meals.length}-${trackedCount}-${lastMealId}`;
}

// ============================================
// Public API
// ============================================

/**
 * Main function to get user insights
 * @param meals - Array of meals to analyze
 * @param forceRefresh - Whether to bypass cache
 */
export async function getUserInsights(
  meals: Meal[],
  forceRefresh = false
): Promise<InsightsData | null> {
  const mealsWithResponses = meals.filter((m) => m.responseResult);

  // Return null only if absolutely no data
  if (meals.length === 0) {
    return null;
  }

  // For limited data, skip AI and use local insights with "Keep Tracking" tip
  const hasLimitedData = meals.length < 5 || mealsWithResponses.length < 3;

  const cacheKey = generateCacheKey(meals);
  const uid = Firebase.auth.currentUser?.uid || null;
  const storageKey = uid ? getInsightsCacheKey(uid) : null;

  // Check cache - invalidates if meals or profile change
  if (!forceRefresh && storageKey) {
    try {
      const cached = await AsyncStorage.getItem(storageKey);
      if (cached) {
        const parsedCache: CachedInsights = JSON.parse(cached);
        const cacheAge = Date.now() - parsedCache.cachedAt;

        // Return cached if still valid and cache key matches
        if (cacheAge < INSIGHTS_CACHE_DURATION && parsedCache.mealsHash === cacheKey) {
          return {
            ...parsedCache.data,
            lastUpdated: new Date(parsedCache.data.lastUpdated),
          };
        }
      }
    } catch (error) {
      console.error('Error reading insights cache:', error);
    }
  }

  // Run analysis
  const analysis = analyzePatterns(meals);

  // Generate insights
  // For limited data, use local insights directly (includes "Keep Tracking" tip)
  // For sufficient data, use AI with local fallback
  const insights = hasLimitedData
    ? generateLocalInsights(analysis)
    : await generateAIInsights(analysis);

  const insightsData: InsightsData = {
    insights,
    lastUpdated: new Date(),
    mealsAnalyzed: meals.length,
    responsesTracked: mealsWithResponses.length,
  };

  // Cache the results (only when we know which user they belong to)
  if (storageKey) {
    try {
      const cacheData: CachedInsights = {
        data: insightsData,
        cachedAt: Date.now(),
        mealsHash: cacheKey,
      };
      await AsyncStorage.setItem(storageKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching insights:', error);
    }
  }

  return insightsData;
}

/**
 * Clear insights cache (call when user tracks new responses, and on sign-out)
 * @param uid - User whose cache to clear; defaults to the current user
 */
export async function clearInsightsCache(uid?: string): Promise<void> {
  try {
    const targetUid = uid || Firebase.auth.currentUser?.uid;
    const keys: string[] = [INSIGHTS_CACHE_KEY]; // legacy unscoped key
    if (targetUid) keys.push(getInsightsCacheKey(targetUid));
    await AsyncStorage.multiRemove(keys);
  } catch (error) {
    console.error('Error clearing insights cache:', error);
  }
}
